import { useRef, useState } from 'react'
import type { AgentMessage, HoistedSkillInfo } from '../agent'
import useAgentSkills, { useSkillBundle } from './useAgentSkills'
import useSkillTestRun from './useSkillTestRun'
import useRouter from './useRouter'
import type { SkillDraft, SkillDuplicateLevel, SkillInfo } from '../types/agent'

/**
 * What "Distil from this chat" sends. The agent proposes one to three
 * candidate skills, asks which to draft (ask_user), and only then calls
 * hoist_skill for each pick; the user reviews each draft in the Skills panel.
 */
export const DISTIL_PROMPT = [
  'Look back over this chat and propose reusable skills from it. Do not call hoist_skill yet.',
  'Write up one to three candidates in your reply, one "### <kebab-name>" section each, with these parts: When to use, Inputs, Files (each file the task needs again by its path: the templates, manifests and configs, scripts and reference data this chat used or produced, and the step that uses it), Steps (numbered, explicit instructions a fresh chat can follow without this one), Checks.',
  'Then call ask_user once: the question "Have I read this chat right? Pick the skills to draft, or tell me what to change.", one option per skill (label and value are its kebab-name, description is its When to use), then a last option labelled "None of these, I will say what to change" with value none (ask_user needs at least two options), multi_select on, free text on. If I pick only none, or type changes, revise and ask again. Then stop and wait for my answer.',
  'When I answer, call hoist_skill once for each skill I picked. If I typed changes instead, revise the candidates and ask again.',
  "I'll review and edit each draft in the Skills panel.",
].join(' ')

export const NO_CHAT_YET = 'Start a chat first: the agent distils a skill from what the chat did.'
export const NOTHING_TO_DISTIL = 'This chat has nothing to distil yet.'

/**
 * True once the chat holds a finished agent turn: an assistant message with
 * text or tool calls that did not fail, and which is not the reply still
 * streaming (the last message while streaming is the open turn).
 */
export function hasCompletedTurn(messages: AgentMessage[], isStreaming: boolean): boolean {
  const lastUser = messages.map((m) => m.role).lastIndexOf('user')
  return messages.some((m, i) => {
    if (m.role !== 'assistant' || m.failed) return false
    if (!m.content.trim() && !m.toolCalls?.length) return false
    return !isStreaming || i < lastUser
  })
}

/** The chat message that asks the agent to install a skill. */
export function installMessage(skill: SkillInfo): string {
  const id = skill.id.startsWith('core:') ? '' : ` (id ${skill.id})`
  return `Please install the skill \`${skill.name}\`${id} into this chat.`
}

// A distilled draft opens its editor once, however often the Skills tab is
// mounted again (the panel unmounts a tab that is not showing).
const openedHoists = new Set<string>()

export interface SkillsAreaInput {
  customer: string
  /** The chat the area sits in; empty on the start page and Admin. */
  sessionId?: string
  /** The jobs a test chat works on: the current chat's. Empty: no test chat. */
  job?: string
  jobs?: string[]
  /** The chat's last skill_hoisted: its draft opens in the editor when it lands. */
  hoisted?: HoistedSkillInfo | null
  /** Sends a message into the current chat (Distil, Install). Absent: neither is offered. */
  onSendMessage?: (message: string) => void
  /** The chat holds a finished agent turn; false disables Distil with NOTHING_TO_DISTIL. */
  hasCompletedTurn?: boolean
}

/**
 * The Skills area's controller (B2), shared by the agent panel's Skills tab
 * and Admin → Skills: the library, which skill is being viewed or edited
 * (and its files), and every action the library and the editor offer.
 */
export function useSkillsArea({ customer, sessionId, job = '', jobs = [], hoisted = null, onSendMessage, hasCompletedTurn: completedTurn }: SkillsAreaInput) {
  const router = useRouter()
  const library = useAgentSkills({ customer, sessionId, job, hoisted })
  const testRun = useSkillTestRun(customer)

  // The row is kept with the id so a skill just created or copied can open
  // before the library has refetched it; the library's row wins once it has.
  const [viewing, setViewing] = useState<SkillInfo | null>(null)
  const [editing, setEditing] = useState<{ row: SkillInfo | null } | null>(null)

  const rows = [...library.company, ...library.bayesprice, ...library.drafts, ...library.mine, ...library.colleagues]
  const fresh = (row: SkillInfo | null) => (row ? rows.find((r) => r.id === row.id) ?? row : null)
  const viewingRow = fresh(viewing)
  const editingRow = editing ? fresh(editing.row) : null

  // Open the draft a hoist produced, once.
  const hoistKey = hoisted && library.hoistedDraft ? `${sessionId}|${hoisted.name}|${hoisted.at}` : ''
  const lastHoistRef = useRef('')
  if (hoistKey && lastHoistRef.current !== hoistKey && !openedHoists.has(hoistKey)) {
    lastHoistRef.current = hoistKey
    openedHoists.add(hoistKey)
    setEditing({ row: library.hoistedDraft! })
  }

  const distilReason = !(onSendMessage && sessionId) ? NO_CHAT_YET : completedTurn === false ? NOTHING_TO_DISTIL : undefined

  const viewBundle = useSkillBundle(viewingRow, customer)
  const editBundle = useSkillBundle(editingRow, customer)

  const save = async (draft: SkillDraft): Promise<SkillInfo> => {
    const saved = editingRow ? await library.handleUpdate(editingRow.id, draft) : await library.handleCreate(draft)
    setEditing({ row: saved })
    return saved
  }

  return {
    ...library,
    viewing: viewingRow,
    viewFiles: viewBundle.data?.files,
    viewError: viewBundle.error ? viewBundle.error.message : null,
    editing: editing !== null,
    editingSkill: editingRow,
    editFiles: editBundle.data?.files,
    editError: editBundle.error ? editBundle.error.message : null,
    testing: testRun.isRunning,
    distilReason,
    canDistil: !distilReason,
    canTest: jobs.length > 0,

    handleView: (skill: SkillInfo) => setViewing(skill),
    handleCloseView: () => setViewing(null),
    handleEdit: (skill: SkillInfo) => { setViewing(null); setEditing({ row: skill }) },
    handleNew: () => setEditing({ row: null }),
    handleCloseEdit: () => setEditing(null),
    handleDistil: () => { if (!distilReason) onSendMessage?.(DISTIL_PROMPT) },
    handleInstall: onSendMessage ? (skill: SkillInfo) => { setViewing(null); onSendMessage(installMessage(skill)) } : undefined,
    handleCopySkill: async (skill: SkillInfo) => {
      const copy = await library.handleCopy(skill.id)
      setViewing(null)
      setEditing({ row: copy })
    },
    handleDuplicateSkill: async (skill: SkillInfo, level: SkillDuplicateLevel) => {
      const copy = await library.handleDuplicate(skill.id, level)
      setViewing(null)
      setEditing({ row: copy })
    },
    handleSaveDraft: save,
    handlePublishDraft: async (draft: SkillDraft) => {
      const saved = await save(draft)
      await library.handlePublish(saved.id)
      setEditing(null)
    },
    handlePublishDraftToBayesPrice: async (draft: SkillDraft) => {
      const saved = await save(draft)
      await library.handlePublishToBayesPrice(saved.id)
      setEditing(null)
    },
    handleSaveChanges: async (draft: SkillDraft) => {
      await save(draft)
      setEditing(null)
    },
    handleDiscard: async () => {
      if (editingRow?.status === 'draft') await library.handleDelete(editingRow.id)
      setEditing(null)
    },
    handleRunTest: async (draft: SkillDraft, prompt: string) => {
      const target = editingRow ?? await library.handleCreate(draft)
      const save = target.status === 'draft'
      await testRun.handleRunTest({ skill: target, draft, prompt, job: job || jobs[0] || '', jobs, save })
    },
    handleOpenTest: (testSessionId: string) => router.navigate('agent-session', { customer, sessionId: testSessionId }),
  }
}

export default useSkillsArea
